export enum LocationNodeType {
    Site = 10,
    Campus = 20,
    Building = 30,
    Block = 40,
    Floor = 50,
    Zone = 60,
    Room = 70,
    Area = 80,
    Aisle = 90,
    Rack = 100,
    Shelf = 110,
    Bin = 120,
    Cabinet = 130,
    Drawer = 140
}

export enum LocationNodeCapability {
    None = 0,
    CanContainChildren = 1,
    CanHaveMap = 2,
    CanBeSelectedAsScope = 4,
    CanHostGate = 8
}

export interface LocationNodePathSegment {
    id: string;
    type: LocationNodeType;
    name: string;
    depth: number;
}

export interface LocationNodeSummary {
    id: string;
    parentId?: string;
    type: LocationNodeType;
    name: string;
    displayPath: string;
    sortOrder: number;
    depth: number;
    hasChildren: boolean;
    capabilities: LocationNodeCapability;
}

export interface LocationNodeTree extends LocationNodeSummary {
    children: LocationNodeTree[];
}

export interface LocationNodeMapContext {
    parentId: string;
    references: LocationNodeMapReference[];
}

export interface LocationNodeMapReference {
    id: string;
    parentId?: string;
    type: LocationNodeType;
    name: string;
    displayPath: string;
    geoJSON?: string;
    overlayImage?: string;
    isParent: boolean;
}

export interface LocationNodeResponse extends LocationNodeSummary {
    shortName?: string;
    code?: string;
    description?: string;
    photoUrl?: string;
    address?: string;
    longitude?: number;
    latitude?: number;
    geoJSON?: string;
    overlayImage?: string;
    zoomLevel: number;
    capacity?: number;
    locationDeterminationStrategy?: number;
    alertMode?: number;
    groupId?: string;
    attributesJson?: string;
    path: LocationNodePathSegment[];
    gates: LocationNodeGateSummary[];
    readers: LocationNodeReaderSummary[];
}

export interface LocationNodeGateSummary {
    id: string;
    name: string;
    directionCount: number;
    readerCount: number;
}

export interface LocationNodeReaderSummary {
    id: string;
    name: string;
    connectionValue?: string;
    isOnline: boolean;
    gateNames: string[];
}

export interface LocationNodeEdit {
    id?: string;
    parentId?: string;
    type?: LocationNodeType;
    name: string;
    shortName?: string;
    code?: string;
    description?: string;
    sortOrder?: number;
    photoUrl?: string;
    address?: string;
    longitude?: number;
    latitude?: number;
    geoJSON?: string;
    overlayImage?: string;
    zoomLevel?: number;
    capacity?: number;
    locationDeterminationStrategy?: number;
    alertMode?: number;
    groupId?: string;
    attributesJson?: string;
}

export interface LocationNodeTypeDefinition {
    type: LocationNodeType;
    displayKey: string;
    icon: string;
    capabilities: LocationNodeCapability;
}

export interface LocationNodeMove {
    parentId?: string;
    sortOrder: number;
}

export interface LocationNodeDeleteImpact {
    id: string;
    descendantCount: number;
    gateCount: number;
    tagCount: number;
    trackItemCount: number;
    ruleRelationCount: number;
    accessHistoryCount: number;
    accessRuleCount: number;
    assemblySessionCount: number;
    objectSafetyCount: number;
    stocktakingCount: number;
    stocktakingItemCount: number;
    hasDependencies: boolean;
}

export interface LocationSelection {
    nodeId: string;
    includeDescendants: boolean;
}

export function flattenLocationTree(nodes: LocationNodeTree[]): LocationNodeTree[] {
    const flattened: LocationNodeTree[] = [];
    const visit = (items: LocationNodeTree[]): void => {
        items.forEach(item => {
            flattened.push(item);
            visit(item.children ?? []);
        });
    };
    visit(nodes ?? []);
    return flattened;
}

export function getLocationNodeTypeLabel(type: LocationNodeType): string {
    return LocationNodeType[type] ?? type.toString();
}
